import PropTypes from 'prop-types';
import { useState, useRef } from 'react';
import styled from 'styled-components';

import { primaryShadow } from '@hivebrite/design-system/Theme';
import { useOnClickOutside } from '@hivebrite/react-hooks';

import {
  prepareItems,
  getMenuItemElements,
} from 'hb-react/frontoffice/apps/Menu/utils';
import onEnterOrSpacePress from 'hb-react/shared/utils/onEnterOrSpacePress';
import keyCodes from 'helpers/keyCodes';

import Item from './Item';
import Skeleton from './Skeleton';

const MenuComponent = ({
  ariaLabel = __('Menu'),
  dynamic = false,
  getPageUrl,
  items: itemsProp,
  onItemClick,
  rootPath,
  testIdLabel = 'menu',
}) => {
  const [menuIndex, setMenuIndex] = useState();
  const wrapperRef = useRef();
  let items = itemsProp || [];

  useOnClickOutside(wrapperRef, () => setMenuIndex(undefined));

  const handleOnBlur = (e) => {
    if (
      e?.target.dataset.itemType !== 'menu-item' &&
      e?.target.dataset.itemType !== 'sub-menu-item'
    ) {
      setMenuIndex(undefined);
    }
  };

  if (dynamic) {
    items = prepareItems(items, getPageUrl);
  }

  const handleOnKeyPress = ({ e, index, isSubItem, subItemIndex }) => {
    switch (e.keyCode) {
      case keyCodes.escape:
        e.preventDefault();
        setMenuIndex(undefined);
        break;
      case keyCodes.arrowLeft:
        e.preventDefault();
        focusPreviousElement({ e, index, isSubItem, subItemIndex });
        break;
      case keyCodes.arrowUp:
        e.preventDefault();
        handleArrowDownUp({ e, index, isSubItem, isDown: false });
        break;
      case keyCodes.arrowRight:
        e.preventDefault();
        focusNextElement({ e, index, isSubItem, subItemIndex });
        break;
      case keyCodes.arrowDown:
        e.preventDefault();
        handleArrowDownUp({ e, index, isSubItem, isDown: true });
        break;
      default:
        break;
    }
  };

  const focusNextElement = ({ e, index, isSubItem, subItemIndex }) => {
    const parentElement = e?.target.closest('[data-item-type="menu"]');

    if (parentElement) {
      const candidateElements = getMenuItemElements(parentElement, 'menu-item');

      if (candidateElements.length) {
        const indexToUse = isSubItem ? subItemIndex : index;
        const candidateIndex =
          indexToUse === candidateElements.length - 1 ? 0 : indexToUse + 1;
        const nextElementToBeFocused = candidateElements[candidateIndex];

        nextElementToBeFocused?.focus();
        setMenuIndex(candidateIndex);
      }
    }
  };

  const focusPreviousElement = ({ e, index, isSubItem, subItemIndex }) => {
    const parentElement = e?.target.closest('[data-item-type="menu"]');

    if (parentElement) {
      const candidateElements = getMenuItemElements(parentElement, 'menu-item');

      if (candidateElements.length) {
        const indexToUse = isSubItem ? subItemIndex : index;
        const candidateIndex =
          indexToUse === 0 ? candidateElements.length - 1 : indexToUse - 1;
        const nextElementToBeFocused = candidateElements[candidateIndex];

        nextElementToBeFocused?.focus();
        setMenuIndex(candidateIndex);
      }
    }
  };

  const focusNextSubElement = (e, index) => {
    const parentElement = e?.target?.parentElement?.parentElement;

    if (parentElement) {
      const candidateElements = getMenuItemElements(
        parentElement,
        'sub-menu-item',
      );

      if (candidateElements.length) {
        const nextElementToBeFocused =
          index === candidateElements.length - 1
            ? candidateElements[0]
            : candidateElements[index + 1];

        nextElementToBeFocused?.focus();
      }
    }
  };

  const focusPreviousSubElement = (e, index) => {
    const parentElement = e?.target?.parentElement?.parentElement;

    if (parentElement) {
      const candidateElements = getMenuItemElements(
        parentElement,
        'sub-menu-item',
      );

      if (candidateElements.length) {
        const nextElementToBeFocused =
          index === 0
            ? candidateElements[candidateElements.length - 1]
            : candidateElements[index - 1];

        nextElementToBeFocused?.focus();
      }
    }
  };

  const handleArrowDownUp = ({ e, index, isSubItem, isDown }) => {
    const parentElement = e?.target?.parentElement?.parentElement;

    if (parentElement) {
      if (!isSubItem) {
        const subMenuItems = getMenuItemElements(
          parentElement,
          'sub-menu-item',
        );

        subMenuItems[isDown ? 0 : subMenuItems.length - 1]?.focus();
      } else if (isDown) {
        focusNextSubElement(e, index);
      } else {
        focusPreviousSubElement(e, index);
      }
    }
  };

  const handleOnItemClick = (e) => {
    e.stopPropagation();

    setMenuIndex(undefined);
    onItemClick();
  };

  return (
    <nav aria-label={ariaLabel} data-testid={testIdLabel} ref={wrapperRef}>
      <ul data-item-type="menu" role="menubar">
        {items.map((item, i) => {
          const subItems = item?.subItems || [];
          const hasSubItems = subItems.length > 0;

          return (
            <MenuItem
              data-item-type="menu-item"
              key={item.title}
              role="none"
              onMouseOver={() => setMenuIndex(i)}
              onMouseOut={() => setMenuIndex(undefined)}
            >
              <Item
                {...item}
                hasSubItems={hasSubItems}
                onBlur={handleOnBlur}
                onClick={handleOnItemClick}
                onFocus={() => setMenuIndex(i)}
                onKeyDown={(e) =>
                  handleOnKeyPress({ e, index: i, isSubItem: false })
                }
                onKeyPress={onEnterOrSpacePress(handleOnItemClick)}
                rootPath={rootPath}
                subItems={subItems}
              >
                {item.title}
              </Item>

              {hasSubItems && (
                <SubmenuWrapper
                  aria-label={item.title}
                  data-testid="sub-menu"
                  hidden={i !== menuIndex}
                  role="menu"
                >
                  {item.subItems.map((subItem, subI) => (
                    <SubmenuItem
                      data-item-type="sub-menu-item"
                      key={subItem.title}
                      role="none"
                    >
                      <Item
                        {...subItem}
                        isSubmenu
                        onBlur={handleOnBlur}
                        onClick={handleOnItemClick}
                        onKeyDown={(e) =>
                          handleOnKeyPress({
                            e,
                            index: subI,
                            isSubItem: true,
                            subItemIndex: i,
                          })
                        }
                        onKeyPress={onEnterOrSpacePress(handleOnItemClick)}
                      >
                        {subItem.title}
                      </Item>
                    </SubmenuItem>
                  ))}
                </SubmenuWrapper>
              )}
            </MenuItem>
          );
        })}
      </ul>
    </nav>
  );
};

const Menu = ({ isLoading = false, ...props }) => {
  if (isLoading) {
    return <Skeleton />;
  }

  return <MenuComponent {...props} />;
};

Menu.propTypes = {
  ariaLabel: PropTypes.string,
  dynamic: PropTypes.bool,
  getPageUrl: PropTypes.func,
  isLoading: PropTypes.bool,
  items: PropTypes.array,
  onItemClick: PropTypes.func,
  rootPath: PropTypes.string,
  testIdLabel: PropTypes.string,
};

export default Menu;

const SubmenuWrapper = styled('ul').withConfig({
  shouldForwardProp: (prop) => !['hidden'].includes(prop),
})`
  background-color: ${(props) => props.theme.colors.white};
  border: 0;
  ${primaryShadow}
  display: ${(props) => (props.hidden ? 'none' : 'block')};
  margin-top: 0;
  padding: 2px 0;
  position: absolute;
  white-space: nowrap;
  z-index: 2;
`;

const MenuItem = styled('li')`
  display: inline-block;
  line-height: 19px;
`;

const SubmenuItem = styled('li')`
  background-color: ${(props) => props.theme.colors.white};

  a {
    width: 100%;

    &:focus,
    &:focus-visible {
      background-color: ${(props) => props.theme.colors.link};
      color: ${(props) => props.theme.colors.white} !important;
      outline: 0;
    }
  }

  &:focus-visible {
    outline: 0;
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.link};

    a {
      color: ${(props) => props.theme.colors.white} !important;
    }
  }
`;
